import React, { useCallback, useEffect, useState } from "react";
import {
  CheckCircleIcon,
  XCircleIcon,
  BanknotesIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";
import {
  listWithdrawals,
  approveWithdrawal,
  rejectWithdrawal,
} from "../../api/finansAgro";

const STATUS_BADGE = {
  pending: "bg-yellow-500/10 text-yellow-500 border-yellow-500/30",
  approved: "bg-blue-500/10 text-blue-500 border-blue-500/30",
  rejected: "bg-red-500/10 text-red-500 border-red-500/30",
  completed: "bg-green-500/10 text-green-500 border-green-500/30",
};

const formatAmount = (n) =>
  new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 0 }).format(n || 0);

const formatDate = (iso) => (iso ? new Date(iso).toLocaleString("ru-RU") : "—");

const RejectModal = ({ withdrawalId, onClose, onConfirm }) => {
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!reason.trim()) return;
    setSubmitting(true);
    try {
      await onConfirm(withdrawalId, reason.trim());
      onClose();
    } catch (e) {
      alert(`Reject failed: ${e.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="dark-card rounded-xl p-6 max-w-md w-full">
        <h3 className="text-lg font-semibold text-white mb-4">
          Reject withdrawal #{withdrawalId}
        </h3>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Себебі (міндетті)"
          className="w-full p-3 rounded-lg bg-bg-primary border border-gray-700 text-white focus:outline-none focus:border-red-500"
          rows={4}
        />
        <div className="flex justify-end gap-3 mt-4">
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 rounded-lg border border-gray-700 text-text-secondary hover:bg-bg-primary"
          >
            Болдырмау
          </button>
          <button
            onClick={submit}
            disabled={!reason.trim() || submitting}
            className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
          >
            {submitting ? "..." : "Reject"}
          </button>
        </div>
      </div>
    </div>
  );
};

const WithdrawalsReview = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rejectFor, setRejectFor] = useState(null);
  const [actingId, setActingId] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listWithdrawals({ status: statusFilter, page, limit: 20 });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, page]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleApprove = async (id) => {
    if (!window.confirm(`Approve withdrawal #${id}?`)) return;
    setActingId(id);
    try {
      await approveWithdrawal(id);
      await fetchData();
    } catch (e) {
      alert(`Approve failed: ${e.message}`);
    } finally {
      setActingId(null);
    }
  };

  const handleReject = async (id, reason) => {
    setActingId(id);
    try {
      await rejectWithdrawal(id, reason);
      await fetchData();
    } finally {
      setActingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-accent-grad rounded-xl flex items-center justify-center">
            <BanknotesIcon className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">Withdrawals</h2>
            <p className="text-sm text-text-secondary">
              Барлығы: {total}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            className="px-3 py-2 rounded-lg bg-bg-primary border border-gray-700 text-white text-sm focus:outline-none"
          >
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="completed">Completed</option>
            <option value="">Барлығы</option>
          </select>
          <button
            onClick={fetchData}
            disabled={loading}
            className="p-2 rounded-lg border border-gray-700 text-text-secondary hover:bg-bg-primary"
            title="Refresh"
          >
            <ArrowPathIcon
              className={`w-5 h-5 ${loading ? "animate-spin" : ""}`}
            />
          </button>
        </div>
      </div>

      {error && (
        <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400">
          {error}
        </div>
      )}

      <div className="dark-card rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-bg-primary text-text-secondary text-sm">
              <tr>
                <th className="px-4 py-3 text-left">ID</th>
                <th className="px-4 py-3 text-left">User</th>
                <th className="px-4 py-3 text-right">Сома</th>
                <th className="px-4 py-3 text-left">Bank</th>
                <th className="px-4 py-3 text-left">Статус</th>
                <th className="px-4 py-3 text-left">Дата</th>
                <th className="px-4 py-3 text-right">Действие</th>
              </tr>
            </thead>
            <tbody>
              {loading && items.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-text-secondary">
                    Жүктелуде...
                  </td>
                </tr>
              )}
              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-text-secondary">
                    Сұрау жоқ
                  </td>
                </tr>
              )}
              {items.map((w) => {
                const bd = w.bank_details || {};
                const badge = STATUS_BADGE[w.status] || STATUS_BADGE.pending;
                return (
                  <tr key={w.id} className="border-t border-gray-800 hover:bg-bg-primary/40">
                    <td className="px-4 py-3 text-white font-mono">#{w.id}</td>
                    <td className="px-4 py-3 text-white">{w.user_id}</td>
                    <td className="px-4 py-3 text-right text-white font-semibold">
                      {formatAmount(w.amount)} ₸
                    </td>
                    <td className="px-4 py-3 text-text-secondary text-sm">
                      <div>{bd.name || "—"}</div>
                      <div className="font-mono text-xs">{bd.iban || "—"}</div>
                      <div className="text-xs">{bd.bik || ""}{bd.bin ? ` · BIN ${bd.bin}` : ""}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-xs font-semibold border ${badge}`}>
                        {w.status}
                      </span>
                      {w.rejection_reason && (
                        <div className="text-xs text-text-secondary mt-1">{w.rejection_reason}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-text-secondary text-sm">
                      {formatDate(w.created_at)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {w.status === "pending" ? (
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => handleApprove(w.id)}
                            disabled={actingId === w.id}
                            className="p-2 rounded-lg bg-green-600/20 text-green-400 hover:bg-green-600/30 disabled:opacity-50"
                            title="Approve"
                          >
                            <CheckCircleIcon className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => setRejectFor(w.id)}
                            disabled={actingId === w.id}
                            className="p-2 rounded-lg bg-red-600/20 text-red-400 hover:bg-red-600/30 disabled:opacity-50"
                            title="Reject"
                          >
                            <XCircleIcon className="w-5 h-5" />
                          </button>
                        </div>
                      ) : (
                        <span className="text-xs text-text-secondary">
                          {w.reviewed_at ? formatDate(w.reviewed_at) : ""}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {total > 20 && (
        <div className="flex items-center justify-between">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-4 py-2 rounded-lg border border-gray-700 text-white disabled:opacity-30"
          >
            Алдыңғы
          </button>
          <span className="text-text-secondary text-sm">Бет {page}</span>
          <button
            onClick={() => setPage((p) => p + 1)}
            disabled={page * 20 >= total}
            className="px-4 py-2 rounded-lg border border-gray-700 text-white disabled:opacity-30"
          >
            Келесі
          </button>
        </div>
      )}

      {rejectFor !== null && (
        <RejectModal
          withdrawalId={rejectFor}
          onClose={() => setRejectFor(null)}
          onConfirm={handleReject}
        />
      )}
    </div>
  );
};

export default WithdrawalsReview;
